import React, { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { UniversesLayoutProps } from "./universesLayout.model";
import { Tags, Universes } from "./Content";
import { useOnboardingStore } from "@companion-core/shared/app/Store/onboardingStore";
import { APIUniverse } from "@companion-core/shared/app/Interfaces/API";
import { Proposal } from "./Content/Proposal";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";
import { useHeaderStore } from "@companion-core/shared/app/Store/header";
import { useGoToGeolocation, useGoToRecommendedList } from "./hooks/useUniversesLayout";

interface UniversesLayoutState {
  step: "universes" | "tags" | "proposal";
  universe?: APIUniverse;
}

export const UniversesLayout = ({ skipAction, universes }: UniversesLayoutProps) => {
  const { t } = useTranslation();
  const [currentState, setCurrentState] = useState<UniversesLayoutState>({
    step: "universes",
  });
  const { setUniverse, setTags, setContentFilters } = useOnboardingStore();
  const { setDisabledHeader } = useHeaderStore();
  const goToGeolocation = useGoToGeolocation();
  const goToRecommendedList = useGoToRecommendedList();

  useMountEffect(() => {
    setDisabledHeader(true);
    return () => {
      setDisabledHeader(false);
    };
  });

  const onPressUniverse = useCallback(
    (universe: APIUniverse) => {
      setUniverse(universe);
      setTags(undefined);
      setContentFilters(universe.content_filters || undefined);
      setCurrentState({ step: "tags", universe });
    },
    [setUniverse, setTags, setContentFilters, setCurrentState]
  );

  const skipUniverses = useCallback(() => {
    setTags(undefined);
    skipAction();
  }, [setTags, skipAction]);

  const goBack = useCallback(() => {
    setCurrentState({ step: "universes" });
  }, [setCurrentState]);

  const goBackToTags = useCallback(() => {
    setCurrentState({ step: "tags", universe: currentState.universe });
  }, [setCurrentState, currentState]);

  return (
    <>
      {currentState.step === "universes" && (
        <Universes
          t={t}
          universes={universes}
          onPressUniverse={onPressUniverse}
          skipAction={skipUniverses}
        />
      )}
      {currentState.step === "tags" && currentState.universe && (
        <Tags
          t={t}
          universe={currentState.universe}
          goBack={goBack}
          skipAction={skipUniverses}
          onSubmitTags={() => {
            setCurrentState({ step: "proposal", universe: currentState.universe });
          }}
        />
      )}
      {currentState.step === "proposal" && currentState.universe && (
        <Proposal
          t={t}
          universe={currentState.universe}
          goBack={goBackToTags}
          nextAction={currentState.universe?.is_geolocalize ? goToGeolocation : goToRecommendedList}
          skipAction={skipUniverses}
        />
      )}
    </>
  );
};
